import {
  OverlayRenderer,
  useOverlayQueueContext,
} from "@jugl-web/domain-resources/common/providers/OverlayQueueProvider";
import {
  TaskLocalActionEvent,
  taskLocalAction$,
} from "@jugl-web/domain-resources/tasks/events/taskLocalAction";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { LogMode } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/LogMode";
import {
  ActiveTaskTimeLogModel,
  TaskTimeLogModel,
} from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { CTAPopupHandle } from "@jugl-web/ui-components/cross-platform/CTAPopup";
import { useToast, useTranslations } from "@jugl-web/utils";
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useActiveTimeTrackingNotesCache } from "../../hooks/useActiveTimeTrackingNotesCache";
import { useTimeLogActions } from "../../hooks/useTimeLogActions";
import {
  ActiveTimeTrackingContext,
  ActiveTimeTrackingContextValue,
} from "./ActiveTimeTrackingContext";
import { ActiveTrackingStoppedDialog } from "./components/ActiveTrackingStoppedDialog";

interface ActiveTimeTrackingProviderProps {
  entityId: string;
  children: ReactNode;
  isDisabled?: boolean;
  onNavigateToTask?: (taskId: string, taskBoardId: string | null) => void;
}

const ACTIVE_TRACKING_STOPPED_POPUP_NAMESPACE = "active-tracking-stopped-popup";

/**
 * Provider for active time tracking.
 * It manages the active time tracking for a given entity.
 * It also shows a popup to the user when the previous active tracking has been stopped.
 */
export const ActiveTimeTrackingProvider: FC<
  ActiveTimeTrackingProviderProps
> = ({ entityId, children, isDisabled = false, onNavigateToTask }) => {
  const { enqueueOverlay, isOverlayOpen, replaceCurrentOverlay } =
    useOverlayQueueContext();

  const { getCachedNotes, clearCachedNotes } =
    useActiveTimeTrackingNotesCache();

  const [isLoading, setIsLoading] = useState(false);

  const ctaPopupRef = useRef<CTAPopupHandle | null>(null);

  const { tasksApi } = useRestApiProvider();

  const { data: activeTimeLog, refetch: refetchActiveTimeLog } =
    tasksApi.useGetActiveTimeLogQuery(
      { entityId },
      {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
        pollingInterval: 1000 * 60 * 3, // 3 minutes
        skip: isDisabled,
      }
    );

  const { createTimeLog, updateTimeLog } = useTimeLogActions();

  const { toast } = useToast();
  const { t } = useTranslations();

  const getActiveTrackingStoppedPopupRenderer = useCallback(
    (
      previousActiveTimeLog: ActiveTaskTimeLogModel,
      reasonMessage: string
    ): OverlayRenderer =>
      ({ isOpen, onClose, onAfterClose }) => (
        <ActiveTrackingStoppedDialog
          ref={ctaPopupRef}
          isOpen={isOpen}
          activeTimeLog={previousActiveTimeLog}
          reasonMessage={reasonMessage}
          onClose={onClose}
          onAfterClose={onAfterClose}
          onNavigateToTask={onNavigateToTask}
        />
      ),
    [onNavigateToTask]
  );

  /**
   * Tiny wrapper around the refetchActiveTimeLog function to handle errors gracefully.
   * Only this function will be exposed from the provider to make sure the app doesn't crash.
   */
  const safeRefetchActiveTimeLog =
    useCallback(async (): Promise<ActiveTaskTimeLogModel | null> => {
      try {
        return await refetchActiveTimeLog().unwrap();
      } catch {
        return null;
      }
    }, [refetchActiveTimeLog]);

  /**
   * Stops the active time tracking.
   *
   * Start timer flow is based on this function, so if anything goes wrong,
   * it will throw an error. This way the whole process will be interrupted.
   */
  const stopActiveTimer = useCallback(
    async (silent = false): Promise<ActiveTaskTimeLogModel | null> => {
      if (!activeTimeLog) {
        return null;
      }

      setIsLoading(true);

      let internalActiveTimeLog: ActiveTaskTimeLogModel | null = activeTimeLog;

      try {
        // Refetch the active time log to reduce chance of working with a stale data
        const latestActiveTimeLog = await refetchActiveTimeLog().unwrap();

        // If there is no latest active time log, it probably means
        // the user tries to stop the tracking that has already been stopped
        if (!latestActiveTimeLog) {
          setIsLoading(false);
          return null;
        }

        internalActiveTimeLog = latestActiveTimeLog;
      } catch {
        // Network or server error prevented refetching - can't safely proceed
        setIsLoading(false);
        throw new Error("Failed to get the latest active time log");
      }

      // Proceed with stopping the active tracking
      try {
        const updatedTimeLog = await updateTimeLog({
          entityId,
          taskId: internalActiveTimeLog.taskId,
          timeLogId: internalActiveTimeLog.id,
          model: {
            startDate: internalActiveTimeLog.startDate,
            endDate: new Date(),
            notes: getCachedNotes(internalActiveTimeLog.id),
          },
        });

        clearCachedNotes();

        if (!silent) {
          toast(
            t({
              id: "feedback.time-log-saved",
              defaultMessage: "Time log has been saved",
            })
          );
        }

        // Reconstruct the active time log model as the backend
        // doesn't provide such a data shape from that endpoint
        return {
          ...updatedTimeLog,
          boardId: internalActiveTimeLog.boardId,
          taskId: internalActiveTimeLog.taskId,
          taskName: internalActiveTimeLog.taskName,
        };
      } catch {
        if (!silent) {
          toast(
            t({
              id: "feedback.time-tracking-finish-failed",
              defaultMessage:
                "Failed to finish time tracking. Please try again.",
            }),
            { variant: "error" }
          );
        }

        throw new Error("Failed to finish time tracking");
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeTimeLog,
      clearCachedNotes,
      entityId,
      getCachedNotes,
      refetchActiveTimeLog,
      t,
      toast,
      updateTimeLog,
    ]
  );

  /**
   * Tiny wrapper around the stopActiveTimer function to handle errors gracefully.
   * Only this function will be exposed from the provider to make sure the app doesn't crash.
   */
  const safeStopActiveTimer =
    useCallback(async (): Promise<ActiveTaskTimeLogModel | null> => {
      try {
        return await stopActiveTimer(false);
      } catch {
        return null;
      }
    }, [stopActiveTimer]);

  /**
   * Starts the time tracking for a given task.
   *
   * If there is an active tracking, it will be stopped first and only if this operation succeeds,
   * the new time tracking will be started. This function is fire-and-forget-like,
   * so it doesn't need to throw (can be safely exposed from the provider)
   */
  const startTimer = useCallback(
    async (taskId: string): Promise<TaskTimeLogModel | null> => {
      setIsLoading(true);

      // Ensure no other timer is active (only one timer can be active per entity)
      try {
        const maybeStoppedActiveTimeLog = await stopActiveTimer(true);

        // If the previous active tracking has been stopped, show the alert
        if (maybeStoppedActiveTimeLog) {
          const renderer = getActiveTrackingStoppedPopupRenderer(
            maybeStoppedActiveTimeLog,
            t({
              id: "tasks-page.only-one-timer-can-be-active",
              defaultMessage: "Only one timer can be active",
            })
          );

          if (isOverlayOpen(ACTIVE_TRACKING_STOPPED_POPUP_NAMESPACE)) {
            replaceCurrentOverlay({ renderer });
            ctaPopupRef.current?.bounce();
          } else {
            enqueueOverlay({
              namespace: ACTIVE_TRACKING_STOPPED_POPUP_NAMESPACE,
              renderer,
            });
          }
        }
      } catch {
        // If stopping the active tracking failed, we can't proceed with the flow
        // (as there can be only one active tracking at a time within an entity)
        toast(
          t({
            id: "feedback.time-tracking-start-failed",
            defaultMessage: "Failed to start time tracking. Please try again.",
          }),
          { variant: "error" }
        );

        setIsLoading(false);

        return null;
      }

      try {
        const newTimeLog = await createTimeLog({
          entityId,
          taskId,
          model: {
            startDate: new Date(),
            endDate: null,
            notes: "",
            logMode: LogMode.Auto,
          },
        });

        toast(
          t({
            id: "feedback.time-tracking-started",
            defaultMessage: "Time tracking has been started",
          })
        );

        return newTimeLog;
      } catch {
        toast(
          t({
            id: "feedback.time-tracking-start-failed",
            defaultMessage: "Failed to start time tracking. Please try again.",
          }),
          { variant: "error" }
        );

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      createTimeLog,
      enqueueOverlay,
      entityId,
      getActiveTrackingStoppedPopupRenderer,
      isOverlayOpen,
      replaceCurrentOverlay,
      stopActiveTimer,
      t,
      toast,
    ]
  );

  const handleTaskLocalActionEvent = useCallback(
    (event: TaskLocalActionEvent) => {
      if (
        event.type !== "task-completed" ||
        !activeTimeLog ||
        activeTimeLog.taskId !== event.taskId
      ) {
        return;
      }

      refetchActiveTimeLog();

      const renderer = getActiveTrackingStoppedPopupRenderer(
        activeTimeLog,
        t({
          id: "tasks-page.task-has-been-completed",
          defaultMessage: "Task has been completed",
        })
      );

      if (isOverlayOpen(ACTIVE_TRACKING_STOPPED_POPUP_NAMESPACE)) {
        replaceCurrentOverlay({ renderer });
      } else {
        enqueueOverlay({
          namespace: ACTIVE_TRACKING_STOPPED_POPUP_NAMESPACE,
          renderer,
        });
      }
    },
    [
      activeTimeLog,
      enqueueOverlay,
      getActiveTrackingStoppedPopupRenderer,
      isOverlayOpen,
      refetchActiveTimeLog,
      replaceCurrentOverlay,
      t,
    ]
  );

  useEffect(() => {
    const subscription = taskLocalAction$.subscribe(handleTaskLocalActionEvent);

    return () => {
      subscription.unsubscribe();
    };
  }, [handleTaskLocalActionEvent]);

  const contextValue = useMemo<ActiveTimeTrackingContextValue>(
    () => ({
      // Don't propagate the potentially cached active time log if the provider is disabled
      activeTimeLog: isDisabled ? null : (activeTimeLog ?? null),
      isLoading,
      startTimer,
      stopActiveTimer: safeStopActiveTimer,
      refetchActiveTimeLog: safeRefetchActiveTimeLog,
    }),
    [
      activeTimeLog,
      isDisabled,
      isLoading,
      startTimer,
      safeStopActiveTimer,
      safeRefetchActiveTimeLog,
    ]
  );

  return (
    <ActiveTimeTrackingContext.Provider value={contextValue}>
      {children}
    </ActiveTimeTrackingContext.Provider>
  );
};
